import { motion } from 'framer-motion';
import { Bell, Check, CheckCheck, UserCheck, HelpCircle, UserX } from 'lucide-react';
import { useNotifications } from '../../api/hooks/useNotifications';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverPanel,
  PopoverHeader,
  PopoverBody,
  usePopover,
} from '../overlays/Popover';
import type { Notification, NotificationData } from '../../types';

export function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="relative p-2 rounded-full hover:bg-background-elevated transition-colors"
        >
          <Bell className="w-5 h-5 text-text-secondary" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-primary text-text-inverse text-xs font-medium rounded-full">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </motion.button>
      </PopoverTrigger>

      <PopoverContent align="right">
        <PopoverPanel>
          <PopoverHeader>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-text-primary">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllAsRead()}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Mark all read
                </button>
              )}
            </div>
          </PopoverHeader>

          <PopoverBody>
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 mx-auto mb-2 text-text-secondary/50" />
                <p className="text-sm text-text-secondary">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <NotificationItemWithClose
                  key={notification.id}
                  notification={notification}
                  onRead={() => markAsRead(notification.id)}
                  formatTime={formatTime}
                />
              ))
            )}
          </PopoverBody>
        </PopoverPanel>
      </PopoverContent>
    </Popover>
  );
}

function NotificationItemWithClose({
  notification,
  onRead,
  formatTime,
}: {
  notification: Notification;
  onRead: () => void;
  formatTime: (date: string) => string;
}) {
  const { close } = usePopover();

  const handleClick = () => {
    if (!notification.read_at) {
      onRead();
    }
    if (notification.data.action_url) {
      window.location.href = notification.data.action_url;
    }
    close();
  };

  return (
    <NotificationItem
      notification={notification}
      onClick={handleClick}
      formatTime={formatTime}
    />
  );
}

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
  formatTime: (date: string) => string;
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'user-check': UserCheck,
  'help-circle': HelpCircle,
  'user-x': UserX,
};

const colorMap: Record<NonNullable<NotificationData['color']>, string> = {
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  error: 'bg-error/10 text-error',
  info: 'bg-primary/10 text-primary',
};

function NotificationItem({ notification, onClick, formatTime }: NotificationItemProps) {
  const isUnread = !notification.read_at;
  const { icon, color } = notification.data;
  const IconComponent = icon ? iconMap[icon] : null;
  const colorClasses = color ? colorMap[color] : null;

  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left px-4 py-3 border-b border-border last:border-b-0
        hover:bg-background transition-colors
        ${isUnread ? 'bg-primary/5' : ''}
      `}
    >
      <div className="flex items-start gap-3">
        {/* Icon / Unread indicator */}
        <div className="flex-shrink-0 mt-0.5">
          {IconComponent && colorClasses ? (
            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${colorClasses}`}>
              <IconComponent className="w-4 h-4" />
            </div>
          ) : isUnread ? (
            <div className="w-2 h-2 mt-1 rounded-full bg-primary" />
          ) : (
            <Check className="w-3.5 h-3.5 mt-0.5 text-text-secondary/50" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${isUnread ? 'font-medium text-text-primary' : 'text-text-secondary'}`}>
            {notification.data.title}
          </p>
          <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">
            {notification.data.message}
          </p>
          <p className="text-xs text-text-secondary/70 mt-1">
            {formatTime(notification.created_at)}
          </p>
        </div>
      </div>
    </button>
  );
}
